import { Plus, Search, X } from 'lucide-react';
import { usePermission } from '@/lib/permissions';
import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveSprints } from '@/hooks/use-sprints';
import { useBoardsTaskStats } from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';
import type { BoardListItem } from '@hygge/shared';
import { sprintPace } from '../lib/sprint-pace';

/**
 * The board index, as a grid of small cards.
 *
 * A list of ten full-width rows inside a 460px scroller spent most of a screen
 * saying ten short things. Three columns say the same ten in a third of the
 * height, which is the difference between the index being visible on the page
 * and being something you scroll to find.
 *
 * The count on each card is real: backlog plus active from the server's own
 * per-board stats. The old row printed `taskCounter`, which is the counter the
 * next issue key is minted from — it never went down when work was finished, so
 * it read as "open work" while being nothing of the kind.
 *
 * Both queries here are keyed exactly as the summary above already keyed them,
 * so this whole section costs no extra requests.
 */

interface Props {
  boards: BoardListItem[];
  search: string;
  onSearchChange: (value: string) => void;
  onBoardSelect: (id: string) => void;
  onCreate: () => void;
}

export function BoardsGrid({ boards, search, onSearchChange, onBoardSelect, onCreate }: Props) {
  // `boards.create` is a company-wide code now, and the only one the board UI
  // still reads: a button that leads straight to a 403 is an invitation to file
  // a false bug report.
  const canCreate = usePermission('boards.create');
  const boardIds = useMemo(() => boards.map((b) => b.id), [boards]);
  const statsQueries = useBoardsTaskStats(boardIds);
  const sprintQueries = useActiveSprints(boardIds);

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? boards.filter((b) =>
        [b.name, b.slug, b.projectName, b.departmentName]
          .filter(Boolean)
          .some((field) => field!.toLowerCase().includes(needle)),
      )
    : boards;

  return (
    <section id="boards" className="mt-7 scroll-mt-6 pb-10">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[13px] font-semibold text-foreground">
          Boards
          <span className="ml-1.5 text-[11px] tabular-nums text-muted-foreground">
            {needle ? `${visible.length} of ${boards.length}` : boards.length}
          </span>
        </h2>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search boards"
            className="h-8 w-[220px] pl-8 pr-8 text-xs @2xl:w-[280px]"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="py-8 text-center text-xs text-muted-foreground">
          No boards match &ldquo;{search}&rdquo;
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 @2xl:grid-cols-2 @4xl:grid-cols-3">
          {visible.map((board) => {
            const at = boardIds.indexOf(board.id);
            const stats = statsQueries[at];
            const sprint = sprintQueries[at]?.data ?? null;
            return (
              <BoardCard
                key={board.id}
                board={board}
                open={
                  stats?.isSuccess
                    ? stats.data.byCategory.backlog + stats.data.byCategory.active
                    : null
                }
                loading={stats?.isPending ?? true}
                sprint={sprint}
                onClick={() => onBoardSelect(board.id)}
              />
            );
          })}

          {!needle && canCreate && (
            <button
              type="button"
              onClick={onCreate}
              className="flex min-h-[4.5rem] cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed border-border text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Plus className="h-3.5 w-3.5" />
              New board
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function BoardCard({
  board,
  open,
  loading,
  sprint,
  onClick,
}: {
  board: BoardListItem;
  open: number | null;
  loading: boolean;
  sprint: Parameters<typeof sprintPace>[0] | null;
  onClick: () => void;
}) {
  const pace = sprint ? sprintPace(sprint) : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[4.5rem] cursor-pointer flex-col justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <span className="flex min-w-0 items-baseline gap-2">
        {/* The board's own colour, set in Board Settings — not a hue picked by
            its position in the list, which changed when a board was renamed. */}
        <span
          className="h-2 w-2 shrink-0 self-center rounded-[2px]"
          style={{ background: board.color || 'hsl(var(--muted))' }}
        />
        <span className="truncate text-[13px] font-medium text-foreground">{board.name}</span>
        <span className="ml-auto shrink-0 font-mono text-[10px] tracking-tight text-muted-foreground/70">
          {board.slug}
        </span>
      </span>

      <span className="mt-2 flex items-baseline gap-1.5 text-[11px] text-muted-foreground">
        {loading ? (
          <Skeleton className="h-3 w-16" />
        ) : open === null ? (
          <span className="text-muted-foreground/50">count unavailable</span>
        ) : (
          <span className="tabular-nums">
            <span className="text-foreground/80">{open}</span> open
          </span>
        )}

        {pace && (
          <>
            <span className="text-muted-foreground/40">·</span>
            {/* The state word, not a colour alone: the same judgement the row
                above makes, in the one word that fits here. */}
            <span
              className={cn(
                'truncate',
                pace.isAtRisk ? 'font-medium text-destructive' : 'text-muted-foreground',
              )}
            >
              {pace.isOverrun
                ? 'sprint overran'
                : pace.isBehind
                  ? 'sprint behind'
                  : pace.donePct === null
                    ? 'sprint running'
                    : `sprint ${Math.round(pace.donePct)}%`}
            </span>
          </>
        )}

        {board.departmentName && !board.projectId && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="truncate">{board.departmentName}</span>
          </>
        )}
      </span>
    </button>
  );
}
